import React from "react";
import { useSeo } from "@/hooks/useSeo";
import { Link } from "react-router-dom";
import useApiList from "@/hooks/useApiList";
import CourseCard from "@/components/CourseCard";
import CertificationPathwayVisual from "@/components/CertificationPathwayVisual";
import FeaturedFaculty from "@/components/FeaturedFaculty";
import { MasterclassVideos } from "@/components/MasterclassVideos";
import { VerificationLinks } from "@/pages/VerifyPage";
import { TrustBadgesStrip } from "@/components/TrustBadgesStrip";
import { ArrowRight, Radio, ShieldCheck, Scale, Building2, Loader2, AlertCircle } from "lucide-react";

export default function HomePage() {
  useSeo({ title: "Converged Security Institute - Professional Qualifications in Converged Security Leadership", description: undefined, path: "/" });
  const { data: courses, loading: coursesLoading, error: coursesError, refetch: refetchCourses } = useApiList("/courses");
  const { data: liveAll } = useApiList("/live-sessions");
  const live = liveAll.slice(0, 3);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-12 lg:pt-16 lg:pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">Converged Security Institute · Professional Development</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[var(--ink)]">
              Lead cyber, physical &amp; OT security as <span className="italic text-[var(--terracotta)]">one</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-700 max-w-2xl">
              A structured qualification pathway from converged security foundations to postgraduate executive leadership.
              Certification, applied assessment and executive integration - delivered live by CSI faculty.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/courses" className="btn-terracotta px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2" data-testid="hero-browse-btn">
                View all programmes <ArrowRight size={16}/>
              </Link>
              <Link to="/register" className="chip px-5 py-3" data-testid="hero-join-btn">Create candidate account</Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-xs text-zinc-600 flex-wrap">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14}/> ISO 31000 · 27001 · 22301 aligned</span>
              <span className="inline-flex items-center gap-1.5"><Scale size={14}/> GDPR · NIS2 · DORA · CRA</span>
              <span className="inline-flex items-center gap-1.5"><Building2 size={14}/> 22 capability domains</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-[var(--border-warm)]"
                 style={{ background: "radial-gradient(ellipse at center, #22365F 0%, #0F2A54 55%, #091B38 100%)" }}>
              <img
                src="/badges/ccso.png"
                className="w-full h-[380px] sm:h-[460px] object-contain p-10 sm:p-14 drop-shadow-[0_12px_40px_rgba(201,165,87,0.45)]"
                alt="CCSO Chartered Executive badge"
              />
              <div className="absolute top-4 left-4 bg-[var(--navy)] text-white text-[11px] font-bold uppercase tracking-widest px-2.5 py-1.5 rounded-full inline-flex items-center gap-2">
                <span className="live-dot"/> Chartered Route Open
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[var(--cream-card)] border border-[var(--border-warm)] rounded-2xl p-4 w-64 shadow-[0_12px_30px_-20px_rgba(0,0,0,0.3)] hidden sm:block">
              <div className="eyebrow">Chartered Executive Route</div>
              <div className="font-display text-lg">Direct CCSO Assessment</div>
              <div className="text-xs text-zinc-600 mt-1">€500 · Portfolio & panel · 15+ years experience · partner portal</div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualification Pathway Visual */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <CertificationPathwayVisual />
      </section>

      {/* Programmes list */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="eyebrow mb-1">Programme catalogue</div>
            <h2 className="font-display text-3xl sm:text-4xl">Qualification Components</h2>
          </div>
          <Link to="/courses" className="chip" data-testid="see-all-courses">See all programmes</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {coursesLoading && courses.length === 0 ? (
            <div className="col-span-full py-14 text-center text-zinc-500 inline-flex items-center justify-center gap-2" data-testid="home-courses-loading">
              <Loader2 size={16} className="animate-spin"/> Loading programmes…
            </div>
          ) : coursesError && courses.length === 0 ? (
            <div className="col-span-full py-14 text-center" data-testid="home-courses-error">
              <AlertCircle size={28} className="mx-auto text-[var(--terracotta)] mb-2"/>
              <div className="text-sm text-zinc-700 mb-3">Couldn't reach the catalogue. We'll keep trying automatically.</div>
              <button className="chip inline-flex items-center gap-1" onClick={refetchCourses}>Retry now</button>
            </div>
          ) : (
            courses.map((c) => <CourseCard key={c.id} course={c} />)
          )}
        </div>
      </section>

      {/* Featured faculty */}
      <TrustBadgesStrip className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10"/>
      <FeaturedFaculty />
      <MasterclassVideos className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"/>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="home-verification">
        <div className="eyebrow mb-1">Verification</div>
        <h2 className="font-display text-3xl sm:text-4xl mb-2">Verify a CSI credential</h2>
        <p className="text-sm text-zinc-600 max-w-2xl mb-6">Every CSI credential is recorded in the GCSB public records - independent, authoritative and open to employers.</p>
        <VerificationLinks compact/>
      </section>

      {/* Live cohort strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="rounded-3xl bg-[var(--ink)] text-white p-6 sm:p-10 grid lg:grid-cols-12 gap-8 relative overflow-hidden">
          <div className="lg:col-span-6">
            <div className="eyebrow text-amber-300">Live sessions this quarter</div>
            <h3 className="font-display text-3xl sm:text-4xl mt-2">Scenario clinics, governance labs &amp; executive drills</h3>
            <p className="mt-3 text-zinc-300 max-w-xl text-sm sm:text-base">
              All CSI programmes include live delivery with Prof. Dr. Vladimir Bunic.
              Tabletop exercises, cross-domain drills and C-suite decision challenges.
            </p>
            <Link to="/live" className="btn-terracotta inline-flex items-center gap-2 mt-5 px-5 py-3 rounded-full text-sm font-semibold" data-testid="home-live-btn">
              See the schedule <ArrowRight size={16}/>
            </Link>
          </div>
          <div className="lg:col-span-6 grid gap-3">
            {live.length === 0 && <div className="text-sm text-zinc-400">No live sessions scheduled yet.</div>}
            {live.map((s) => (
              <div key={s.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between">
                <div>
                  <div className="eyebrow text-amber-300">{new Date(s.scheduled_at).toLocaleString()}</div>
                  <div className="font-semibold">{s.title}</div>
                  <div className="text-xs text-zinc-400">{s.course_title}</div>
                </div>
                <span className="inline-flex items-center gap-1 text-xs bg-white text-[var(--ink)] px-2.5 py-1 rounded-full font-bold">
                  <span className="live-dot"/> {s.duration_min}m
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / policy */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-3 gap-6">
          {[
            { h: "Governed credentials", p: "CSI designations are awarded under the CSI Certification Policy with annual CPD, appeals and records-management controls." },
            { h: "Executive register", p: "PGDCSL graduates may use the post-nominal while in good standing and appear in CSI's verifiable register." },
            { h: "Recognition of Prior Learning", p: "Advanced standing is available for eligible components; the Executive Integration Portfolio remains mandatory for all candidates." },
          ].map((b) => (
            <div key={b.h} className="card-warm p-6">
              <div className="eyebrow mb-2">Policy</div>
              <h3 className="font-display text-2xl">{b.h}</h3>
              <p className="text-sm text-zinc-600 mt-2">{b.p}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
